// SPDX-FileCopyrightText: 2016-2026 Lari Natri <lari.natri@iki.fi>
// SPDX-License-Identifier: GPL-3.0-or-later

import {
  richTextDomExtension,
  type TranslatorDomExtensionContext,
  type RichTextTagRenderer,
  type TranslatorDomExtension,
} from "@unilarva/translator/dom";

import { createRichTranslationTagSpecs } from "../../shared/src/rich-translations";
import type { SiteConfig } from "../../shared/src/types";

/** Creates the parent application's semantic rich-translation DOM extension. */
export function createRichTranslationExtension(site: SiteConfig): TranslatorDomExtension {
  const specs = createRichTranslationTagSpecs(site, true);
  const renderers: Record<string, RichTextTagRenderer> = {};
  const renderedLanguageAttribute = "data-i18n-rich-lang";
  const renderedState = new WeakMap<
    Element,
    {
      language: string;
      key: string;
      source: string;
      values: TranslatorDomExtensionContext["values"];
    }
  >();

  for (const [name, spec] of Object.entries(specs)) {
    renderers[name] = (children, { document }) => {
      const element = document.createElement(spec.element);
      if (spec.className) element.className = spec.className;
      if (spec.href) element.setAttribute("href", spec.href);
      if (spec.external) {
        element.setAttribute("target", "_blank");
        element.setAttribute("rel", "noopener noreferrer");
      }
      if (spec.src) element.setAttribute("src", spec.src);
      if (spec.width) element.setAttribute("width", String(spec.width));
      if (spec.height) element.setAttribute("height", String(spec.height));
      if (spec.loading) element.setAttribute("loading", spec.loading);
      if (spec.element === "i") element.setAttribute("aria-hidden", "true");

      if (spec.content === "children") element.appendChild(children);
      else if (spec.content === "alt") element.setAttribute("alt", children.textContent ?? "");
      return element;
    };
  }

  const extension = richTextDomExtension({ renderers });
  return {
    selector: extension.selector,
    update(element, context): void {
      const language = context.translator.getLanguage();
      const key = element.getAttribute("data-i18n-rich")?.trim() ?? "";
      const source = context.translator.translateKey(key, { interpolate: false });
      const hasClientOnlyContent = element.querySelector("[data-i18n-rich-client-only]") !== null;
      const previous = renderedState.get(element);
      if (
        previous?.language === language &&
        previous.key === key &&
        previous.source === source &&
        previous.values === context.values
      ) {
        return;
      }
      if (
        !previous &&
        element.getAttribute(renderedLanguageAttribute) === language &&
        !hasClientOnlyContent &&
        context.values === undefined
      ) {
        renderedState.set(element, { language, key, source, values: context.values });
        return;
      }

      extension.update(element, context);
      element.setAttribute(renderedLanguageAttribute, language);
      renderedState.set(element, { language, key, source, values: context.values });
    },
  };
}
